import React, { useState, useMemo } from "react";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { positionBucket, POSITION_ORDER, isOpenSeat } from "../services/roles";
import { personAssignments, isBenchEligible } from "../services/utilization";
import "../styles.css";

function SummaryCard({ n, label, color }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || "var(--text-primary)" }}>{n}</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function ScenarioPlanner() {
  const { projects, teamMembers, people, loading, error } = useData();
  const pipeline = useMemo(() => (projects || []).filter(p => p.Status === "Potential"), [projects]);
  const [selected, setSelected] = useState(null); // null → treat as "all selected"

  const sel = selected || new Set(pipeline.map(p => p.Title));
  const toggle = (t) => setSelected(prev => {
    const base = prev || new Set(pipeline.map(p => p.Title));
    const n = new Set(base); n.has(t) ? n.delete(t) : n.add(t); return n;
  });
  const selectAll = () => setSelected(new Set(pipeline.map(p => p.Title)));
  const selectNone = () => setSelected(new Set());

  // Available bench headcount by position (people with open capacity).
  const benchByPos = useMemo(() => {
    const m = {};
    (people || []).filter(p => p.IsActive !== false && isBenchEligible(p)).forEach(p => {
      const u = personAssignments(p, teamMembers || [], projects || []);
      if (100 - u.peakUtil > 0) { const b = positionBucket(p.CompanyRole); m[b] = (m[b] || 0) + 1; }
    });
    return m;
  }, [people, teamMembers, projects]);

  // Demand by position from the selected pipeline jobs.
  const { needByPos, expByPos } = useMemo(() => {
    const need = {}, exp = {};
    (teamMembers || []).filter(mm => sel.has(mm.ProjectJobNumber) && isOpenSeat(mm)).forEach(mm => {
      const proj = pipeline.find(p => p.Title === mm.ProjectJobNumber);
      const conf = proj && proj.Confidence != null ? Number(proj.Confidence) / 100 : 1;
      const b = positionBucket(mm.ProjectRole);
      need[b] = (need[b] || 0) + 1;
      exp[b] = (exp[b] || 0) + conf;
    });
    return { needByPos: need, expByPos: exp };
  }, [teamMembers, sel, pipeline]);

  const rows = POSITION_ORDER.filter(b => (needByPos[b] || 0) > 0 || (benchByPos[b] || 0) > 0);
  const totalNeed = Object.values(needByPos).reduce((a, b) => a + b, 0);
  const totalShort = rows.reduce((a, b) => a + Math.max(0, (needByPos[b] || 0) - (benchByPos[b] || 0)), 0);

  if (loading) return <div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading…</div></div>;
  if (error) return <div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div>;

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
        Model your workforce <b>if you win pipeline work</b>. Toggle the prospective jobs below; each bar shows the <b>open seats</b> those jobs need by position versus how many people are free on the bench. <span style={{ color: "var(--red)", fontWeight: 600 }}>Red = the gap you'd have to hire.</span> Define a job's seats in the Planner (Add Job) or Admin → Manage Teams.
      </div>

      {pipeline.length === 0 ? (
        <div className="empty"><div className="empty-icon"><Icon name="calendar" /></div><div className="empty-text">No pipeline (Potential) jobs to model yet. Add one in the Planner or Admin.</div></div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: 4 }}>If we win:</span>
            {pipeline.map(p => {
              const on = sel.has(p.Title);
              return (
                <button key={p.Id} className={`btn ${on ? "btn-primary" : "btn-ghost"}`} style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => toggle(p.Title)}>
                  {on ? "✓ " : ""}{p.JobName || p.Title}{p.Confidence != null ? ` · ${p.Confidence}%` : ""}
                </button>
              );
            })}
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }} onClick={selectAll}>All</button>
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }} onClick={selectNone}>None</button>
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14, marginBottom: 18 }}>
            <SummaryCard n={sel.size} label="Jobs modeled" />
            <SummaryCard n={totalNeed} label="Roles needed" />
            <SummaryCard n={totalShort} label="Would need to hire" color={totalShort > 0 ? "var(--red)" : "var(--green)"} />
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Need vs Bench by Position</div>
              <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--text-secondary)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--green)", borderRadius: 2 }} /> Covered by bench</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--red)", borderRadius: 2 }} /> Must hire</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--border-mid)", borderRadius: 2 }} /> Spare bench</span>
              </div>
            </div>
            {rows.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Select at least one pipeline job to see the projection.</div>
            ) : rows.map(b => {
              const need = needByPos[b] || 0, bench = benchByPos[b] || 0;
              const covered = Math.min(need, bench), short = Math.max(0, need - bench), spare = Math.max(0, bench - need);
              const scale = Math.max(need, bench, 1);
              const seg = (val, color, key) => val > 0 ? <div key={key} style={{ width: `${(val / scale) * 100}%`, background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 800 }}>{(val / scale) > 0.07 ? val : ""}</div> : null;
              return (
                <div key={b} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{b}</span>
                    <span style={{ color: "var(--text-muted)" }}>
                      {need} needed · {bench} on bench
                      {short > 0 && <> · <b style={{ color: "var(--red)" }}>hire {short}</b></>}
                      {expByPos[b] != null && <> · ~{Math.round(expByPos[b] * 10) / 10} expected</>}
                    </span>
                  </div>
                  <div style={{ display: "flex", height: 22, borderRadius: 5, overflow: "hidden", background: "var(--bg-elevated)" }}>
                    {seg(covered, "var(--green)", "c")}
                    {seg(short, "var(--red)", "s")}
                    {seg(spare, "var(--border-mid)", "p")}
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
              "Expected" applies each job's win-confidence to its headcount — a probability-weighted estimate of the hiring you'll actually face.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
